import React, { useEffect, useRef, useState } from "react";
import { loadMercadoPago } from "@mercadopago/sdk-js";
import api from "@/lib/api";
import { Lock, Warning } from "@phosphor-icons/react";

const PUBLIC_KEY = process.env.REACT_APP_MP_PUBLIC_KEY;

export default function MercadoPagoCardForm({ order, onResult }) {
  const containerId = `mp-card-${order.id}`;
  const controller = useRef(null);
  const mounted = useRef(false);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!PUBLIC_KEY || mounted.current) return;
    mounted.current = true;
    let alive = true;
    (async () => {
      try {
        await loadMercadoPago();
        if (!alive) return;
        const mp = new window.MercadoPago(PUBLIC_KEY, { locale: "es-MX" });
        const bricks = mp.bricks();
        controller.current = await bricks.create("cardPayment", containerId, {
          initialization: {
            amount: Number(order.total),
            payer: { email: order.buyer_email || "" },
          },
          customization: {
            paymentMethods: {
              minInstallments: 1,
              maxInstallments: 3,
              types: { included: ["credit_card", "debit_card"] },
            },
            visual: { style: { theme: "default" }, hidePaymentButton: false },
          },
          callbacks: {
            onReady: () => { if (alive) setReady(true); },
            onError: (e) => { if (alive) setError(e?.message || "No se pudo cargar el formulario de tarjeta"); },
            onSubmit: async (cardData) => {
              setError("");
              try {
                const res = await api.post("/payments/card", { order_id: order.id, card_data: cardData });
                onResult({ ok: true, order: res.data });
              } catch (e) {
                const msg = e.response?.data?.detail || "No se pudo procesar el pago";
                setError(msg);
                onResult({ ok: false, message: msg });
                throw new Error(msg);
              }
            },
          },
        });
      } catch (e) { if (alive) setError(e.message); }
    })();
    return () => {
      alive = false;
      try { controller.current?.unmount?.(); } catch (_) {}
    };
  }, [order.id, order.total, order.buyer_email, containerId]);

  if (!PUBLIC_KEY) {
    return (
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 flex items-start gap-3" data-testid="mp-not-configured">
        <Warning weight="fill" size={20} className="text-amber-500 mt-0.5 shrink-0" />
        <div className="text-sm">
          <p className="font-semibold text-amber-800">Pago con tarjeta en configuración</p>
          <p className="text-amber-700 mt-0.5">El pago con tarjeta estará disponible en cuanto se configure la llave pública de Mercado Pago (REACT_APP_MP_PUBLIC_KEY).</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="mp-card-form">
      {error && <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700 mb-3" data-testid="mp-card-error">{error}</div>}
      {!ready && !error && <p className="text-sm text-muted-foreground mb-3 flex items-center gap-2"><Lock weight="bold" size={14} /> Cargando formulario seguro…</p>}
      <div id={containerId} />
    </div>
  );
}
